/**
 * Welcome Landing Page - Cold Luxury Design
 *
 * Dark blue + silver/cyan palette
 * Full content structure with brand icons
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useWelcomeContent } from "./hooks";
import { sanitizeUrl } from "../../lib/url-utils";
import {
  IconMapPin,
  IconChevronRight,
  IconExternalLink,
  IconCalendar,
  IconUsers,
  IconDove,
  IconFoldedHands,
  IconHomeHeart,
  IconBriefcase,
  IconHeartHands,
  IconFamily,
  IconClock,
  IconSparkle,
  IconYouth,
  IconGlobe,
  IconHeartFilled,
  IconEnvelope,
} from "../../components/Icons";
import styles from "./WelcomePage.module.css";

interface Activity {
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
}

const ACTIVITIES: Activity[] = [
  { Icon: IconDove, label: "Богослужения" },
  { Icon: IconFoldedHands, label: "Утренние молитвы" },
  { Icon: IconHomeHeart, label: "Домашние группы" },
  { Icon: IconBriefcase, label: "Бизнес-клуб" },
  { Icon: IconHeartHands, label: "Женские встречи" },
  { Icon: IconFamily, label: "Семейный клуб" },
  { Icon: IconUsers, label: "Совместные завтраки" },
  { Icon: IconSparkle, label: "Мастер-классы" },
  { Icon: IconYouth, label: "Молодёжные встречи" },
  { Icon: IconGlobe, label: "Экскурсии" },
];

export function WelcomePage() {
  const {
    heroVideoUrl,
    heroImageUrl,
    heroTitle,
    heroSubtitle,
    daysCount,
    missionText,
    mapUrl: rawMapUrl,
  } = useWelcomeContent();

  const sectionsRef = useRef<(HTMLElement | null)[]>([]);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [daysAnimated, setDaysAnimated] = useState(0);

  const mapUrl = rawMapUrl ? sanitizeUrl(rawMapUrl) : null;

  useEffect(() => {
    document.documentElement.setAttribute("data-welcome-page", "true");
    return () => {
      document.documentElement.removeAttribute("data-welcome-page");
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (daysCount && daysCount > 0) {
      const duration = 2500;
      const steps = 80;
      const stepValue = daysCount / steps;
      let current = 0;
      const interval = setInterval(() => {
        current += stepValue;
        if (current >= daysCount) {
          setDaysAnimated(daysCount);
          clearInterval(interval);
        } else {
          setDaysAnimated(Math.floor(current));
        }
      }, duration / steps);
      return () => clearInterval(interval);
    }
  }, [daysCount]);

  const defaultHeroTitle = "Добро пожаловать в сердце нашей церкви";
  const defaultHeroSubtitle = "Мы — Первая русскоязычная Евангельская Церковь в Таиланде, и наша история началась именно с острова Пхукет.";
  const defaultMission = "Помогать людям узнавать Бога, строить крепкие семьи, раскрывать своё призвание и быть светом в том месте, где они живут.";

  return (
    <div className={styles.page}>
      {/* =====================================================================
          HERO SECTION
          ===================================================================== */}
      <section className={styles.hero}>
        <div className={styles.heroBackground} />

        {(heroVideoUrl || heroImageUrl) && (
          <div className={styles.heroMedia}>
            {heroVideoUrl ? (
              <video
                src={heroVideoUrl}
                className={`${styles.heroVideo} ${videoLoaded ? styles.loaded : ""}`}
                autoPlay
                muted
                loop
                playsInline
                onLoadedData={() => setVideoLoaded(true)}
              />
            ) : heroImageUrl ? (
              <img
                src={heroImageUrl}
                alt=""
                className={styles.heroImage}
              />
            ) : null}
            <div className={styles.heroOverlay} />
          </div>
        )}

        <div className={styles.heroContent}>
          <div className={styles.heroLogo}>
            <img src="/logo.png" alt="ONE Church" className={styles.heroLogoImg} />
          </div>
          <h1 className={styles.heroTitle}>{heroTitle || defaultHeroTitle}</h1>
          <p className={styles.heroSubtitle}>{heroSubtitle || defaultHeroSubtitle}</p>

          <div className={styles.heroCta}>
            <Link to="/schedule" className={styles.ctaPrimary}>
              <span>Изучить афишу</span>
              <IconChevronRight className={styles.ctaIcon} />
            </Link>
          </div>
        </div>
      </section>

      {/* =====================================================================
          INTRO SECTION - About the community
          ===================================================================== */}
      <section
        className={`${styles.section} ${styles.introSection} ${styles.fadeSection}`}
        ref={(el) => { sectionsRef.current[0] = el; }}
      >
        {/* Days counter overlaps Hero */}
        <div className={styles.daysCounter}>
          <div className={styles.daysRing}>
            <div className={styles.daysRingCircle} />
            <div className={styles.daysInner}>
              <span className={styles.daysNumber}>{daysAnimated}+</span>
              <span className={styles.daysLabel}>дней в молитве</span>
            </div>
          </div>
        </div>

        <div className={styles.container}>
          <p className={styles.introText}>
            С февраля 2023 года мы собираемся вместе, молимся, поддерживаем друг друга 
            и строим живое сообщество людей, для которых вера — это не традиция, а образ жизни.
          </p>

          <div className={styles.introCards}>
            <div className={styles.introCard}>
              <span className={styles.introCardText}>
                Если вы давно в вере — здесь вы найдёте духовную семью.
              </span>
            </div>
            <div className={styles.introCard}>
              <span className={styles.introCardText}>
                Если вы только ищете Бога — здесь можно задавать вопросы.
              </span>
            </div>
            <div className={styles.introCard}>
              <span className={styles.introCardText}>
                Если вы не уверены, во что верите — вы всё равно можете прийти.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================================
          WHO WE ARE SECTION
          ===================================================================== */}
      <section
        className={`${styles.section} ${styles.whoSection} ${styles.fadeSection}`}
        ref={(el) => { sectionsRef.current[1] = el; }}
      >
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>
              <IconHeartFilled className={styles.sectionIconSvg} />
            </span>
            Кто мы<span className={styles.sectionTitleAccent}>?</span>
          </h2>

          <p className={styles.whoLead}>Мы — обычные люди.</p>

          <div className={styles.whoList}>
            <span>Семьи.</span>
            <span>Предприниматели.</span>
            <span>Молодёжь.</span>
          </div>

          <div className={styles.whoDescription}>
            <p>Те, кто переехал на Пхукет.</p>
            <p>Те, кто переживает новый этап жизни.</p>
            <p>Те, кто ищет опору.</p>
          </div>

          <p className={styles.whoConclusion}>
            Нас объединяет желание расти — духовно, лично и как сообщество.
          </p>
        </div>
      </section>

      {/* =====================================================================
          SCHEDULE SECTION
          ===================================================================== */}
      <section
        className={`${styles.section} ${styles.scheduleSection} ${styles.fadeSection}`}
        ref={(el) => { sectionsRef.current[2] = el; }}
      >
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>
              <IconMapPin className={styles.sectionIconSvg} />
            </span>
            Где и когда
          </h2>

          <div className={styles.scheduleCard}>
            <p className={styles.schedulePlace}>
              Наши богослужения проходят в районе Банг Тао, Пхукет
            </p>

            <div className={styles.scheduleTimeBlock}>
              <div className={styles.scheduleItem}>
                <span className={styles.scheduleIcon}>
                  <IconCalendar className={styles.scheduleIconSvg} />
                </span>
                <span className={styles.scheduleValue}>Каждую субботу</span>
              </div>
              <div className={styles.scheduleItem}>
                <span className={styles.scheduleIcon}>
                  <IconClock className={styles.scheduleIconSvg} />
                </span>
                <span className={styles.scheduleValue}>В 13:00</span>
              </div>
            </div>

            <p className={styles.scheduleNote}>
              После служения всегда есть время для общения.
            </p>

            <a
              href={mapUrl || "https://maps.app.goo.gl/yEKa3BFpDPdLCJAo6"}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.mapButton}
            >
              <IconMapPin />
              <span>Построить маршрут</span>
              <IconExternalLink />
            </a>
          </div>
        </div>
      </section>

      {/* =====================================================================
          ACTIVITIES SECTION
          ===================================================================== */}
      <section
        className={`${styles.section} ${styles.activitiesSection} ${styles.fadeSection}`}
        ref={(el) => { sectionsRef.current[3] = el; }}
      >
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>
              <IconSparkle className={styles.sectionIconSvg} />
            </span>
            Что происходит у нас
          </h2>

          <div className={styles.activitiesGrid}>
            {ACTIVITIES.map((activity, index) => (
              <div key={index} className={styles.activityItem}>
                <span className={styles.activityIcon}>
                  <activity.Icon className={styles.activityIconSvg} />
                </span>
                <span className={styles.activityLabel}>{activity.label}</span>
              </div>
            ))}
          </div>

          <p className={styles.activitiesConclusion}>
            Мы верим, что церковь — это не только собрание.<br />
            Это жизнь вместе.
          </p>

          <Link to="/schedule" className={styles.activitiesCta}>
            <span>Смотреть афишу</span>
            <IconChevronRight className={styles.ctaIcon} />
          </Link>
        </div>
      </section>

      {/* =====================================================================
          DOUBTS SECTION
          ===================================================================== */}
      <section
        className={`${styles.section} ${styles.doubtsSection} ${styles.fadeSection}`}
        ref={(el) => { sectionsRef.current[4] = el; }}
      >
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>
              <IconFoldedHands className={styles.sectionIconSvg} />
            </span>
            Если вы сомневаетесь
          </h2>

          <div className={styles.doubtsContent}>
            <p className={styles.doubtsLead}>
              Вы можете просто прийти и познакомиться.<br />
              Без давления и обязательств.
            </p>

            <div className={styles.doubtsList}>
              <p>Вы можете наблюдать.</p>
              <p>Вы можете слушать.</p>
              <p>Вы можете задавать вопросы.</p>
            </div>

            <p className={styles.doubtsConclusion}>
              Мы открыты к диалогу.
            </p>
          </div>
        </div>
      </section>

      {/* =====================================================================
          CTA SECTION
          ===================================================================== */}
      <section
        className={`${styles.section} ${styles.ctaSection} ${styles.fadeSection}`}
        ref={(el) => { sectionsRef.current[5] = el; }}
      >
        <div className={styles.container}>
          <div className={styles.ctaButtons}>
            <Link to="/schedule" className={styles.ctaButtonPrimary}>
              <IconCalendar className={styles.ctaButtonIcon} />
              <span>Афиша мероприятий</span>
            </Link>
            <Link to="/home-groups" className={styles.ctaButtonSecondary}>
              <IconHomeHeart className={styles.ctaButtonIcon} />
              <span>Домашние группы</span>
            </Link>
            <Link to="/feedback" className={styles.ctaButtonSecondary}>
              <IconEnvelope className={styles.ctaButtonIcon} />
              <span>Связаться с нами</span>
            </Link>
          </div>
        </div>
      </section>

      {/* =====================================================================
          MISSION SECTION
          ===================================================================== */}
      <section
        className={`${styles.section} ${styles.missionSection} ${styles.fadeSection}`}
        ref={(el) => { sectionsRef.current[6] = el; }}
      >
        <div className={styles.container}>
          <h2 className={styles.missionTitle}>
            <span className={styles.sectionIcon}>
              <IconDove className={styles.sectionIconSvg} />
            </span>
            Наша миссия
          </h2>

          <p className={styles.missionText}>
            {missionText || defaultMission}
          </p>
        </div>
      </section>

      <div className={styles.footerSpacer} />
    </div>
  );
}
